<template>
  <div class="landing">

    <!-- Sticky CTA: appears only after the hero button has scrolled away, so it never
         duplicates a control that's already on screen. -->
    <transition name="sticky-cta">
      <router-link v-if="showStickyCta" to="/WishboardCreate" class="sticky-cta">
        <button class="btn-cta btn-cta-sm">Create a free Wishboard</button>
      </router-link>
    </transition>

    <!-- HERO -->
    <section class="hero-split">
      <div class="hero-left">
        <div class="use-cases">
          <span v-for="tag in tags" :key="tag" class="tag">{{ tag }}</span>
        </div>
        <h1>The group card your whole team can sign — instantly, online.</h1>
        <p class="hero-sub">
          Share a link. Collect heartfelt messages from everyone.
          Present something truly personal.
        </p>
        <div class="hero-cta" ref="heroCta">
          <router-link to="/WishboardCreate" class="cta-link">
            <button class="btn-cta">Create a free Wishboard</button>
          </router-link>
          <span class="cta-note">Free · No account needed to contribute</span>
        </div>
      </div>

      <!-- Live board with occasion tiles on the right -->
      <div class="hero-right">
        <!-- Occasion tiles sit to the right of the board and are the navigation: click
             one and the board changes. No overlay chrome on the board itself — it should
             read as a real board, not a slideshow widget. -->
        <div class="mock-board" ref="boardEl"
           :style="mockBg ? { backgroundImage: 'url(' + mockBg + ')', backgroundSize: 'cover', backgroundPosition: 'center' } : {}">
          <div class="mock-board-top">
            <div class="mock-board-title">{{ activeBoard.title }}</div>
            <div class="mock-board-meta">{{ activeBoard.meta }}</div>
            <div class="mock-actions">
              <!-- Sample board: this button is a preview, so clicking it should answer
                   politely rather than do nothing. A quiet inline line appears under the
                   board header — no alert, no dead end. -->
              <button class="mock-btn mock-btn-preview" type="button" @click="showSampleNote = true"
                      :aria-expanded="showSampleNote ? 'true' : 'false'">
                <i class="bx bx-plus"></i> Add a wish
              </button>
              <span class="mock-btn-ghost" @click="fireConfetti" title="Share">
                <i class="bx bx-share-alt"></i>
              </span>
            </div>
            <transition name="mock-note-fade">
              <p v-if="showSampleNote" class="mock-invite">
                <i class="bx bx-info-circle"></i>
                This board is a sample. Create your own and everyone can add a real wish.
              </p>
            </transition>
          </div>

          <!-- Background switcher -->
          <div class="bg-switcher">
            <span class="bg-label">Background</span>
            <div class="bg-thumbs">
              <button
                v-for="bg in bgOptions"
                :key="bg.label"
                class="bg-thumb"
                :class="{ active: mockBg === bg.url }"
                :style="{ backgroundImage: 'url(' + bg.thumb + ')' }"
                @click="mockBg = bg.url"
                :title="bg.label"
              ></button>
              <button
                class="bg-thumb bg-thumb-none"
                :class="{ active: !mockBg }"
                @click="mockBg = null"
                title="Default"
              ><i class="bx bx-x"></i></button>
            </div>
          </div>

          <div class="mock-cards">
            <div class="mock-card" v-for="msg in activeBoard.messages" :key="msg.name">
              <div class="mock-from">{{ msg.name }}</div>
              <div class="mock-msg">{{ msg.message }}</div>
              <img v-if="msg.gif" :src="msg.gif" class="mock-gif" alt="" />
            </div>
          </div>

        </div>

        <div class="occ-rail-v" role="tablist" aria-label="Choose an occasion">
          <p class="occ-rail-v-title">Pick an occasion to see how a board looks. Every one
            is fully customisable — colours, fonts and background.</p>
          <!-- Scrolling strip rather than a full column: only a few occasions are visible so
               the hero stays uncluttered, and the masked bottom edge hints there's more. -->
          <div class="occ-rail-scroll" ref="occRail" @scroll.passive="onRailScroll">
            <button
              v-for="(b, i) in boards"
              :key="b.title"
              class="occ-tile"
              :class="{ active: i === boardIndex }"
              :style="{ backgroundImage: 'url(' + b.bg + ')' }"
              role="tab"
              :aria-selected="i === boardIndex ? 'true' : 'false'"
              :title="b.occasion"
              @click="goToBoard(i)"
            >
              <span class="occ-tile-label">{{ b.occasion }}</span>
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- OCCASIONS -->
    <section class="section-pad">
      <p class="overline">Built for every moment</p>
      <h2 class="section-heading">Whatever the occasion, make it memorable.</h2>
      <div class="occasion-grid">
        <div class="occasion" v-for="occ in occasions" :key="occ.icon">
          <i :class="'bx ' + occ.icon + ' occ-icon'"></i>
          <h3>{{ occ.title }}</h3>
          <p>{{ occ.desc }}</p>
        </div>
      </div>
    </section>

    <!-- HOW IT WORKS -->
    <section class="section-alt">
      <div class="section-inner">
        <p class="overline">Simple by design</p>
        <h2 class="section-heading">Up and running in under a minute.</h2>
        <div class="steps">
          <div class="step" v-for="step in steps" :key="step.num">
            <div class="step-num">{{ step.num }}</div>
            <h3>{{ step.title }}</h3>
            <p>{{ step.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- FEATURES -->
    <section class="section-pad">
      <p class="overline">Why Wishboard</p>
      <h2 class="section-heading">Thoughtfully designed, effortlessly shared.</h2>
      <div class="features">
        <div class="feature" v-for="f in features" :key="f.icon">
          <div class="feature-icon-wrap">
            <i :class="'bx ' + f.icon"></i>
          </div>
          <h3>{{ f.title }}</h3>
          <p>{{ f.desc }}</p>
        </div>
      </div>
    </section>

    <!-- TESTIMONIALS -->
    <section class="section-alt">
      <div class="section-inner">
        <p class="overline">Real stories</p>
        <h2 class="section-heading">People love the moments it creates.</h2>
        <div class="testimonials">
          <div class="testimonial" v-for="t in testimonials" :key="t.name">
            <i class="bx bxs-quote-alt-left quote-icon"></i>
            <p class="quote">{{ t.quote }}</p>
            <div class="author">
              <div class="author-avatar">{{ t.name[0] }}</div>
              <div>
                <div class="author-name">{{ t.name }}</div>
                <div class="author-role">{{ t.role }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- BOTTOM CTA -->
    <section class="cta-bottom">
      <h2>Start your first Wishboard today.</h2>
      <p>Free to create. No account needed to contribute.</p>
      <router-link to="/WishboardCreate">
        <button class="btn-cta">Get started — it's free</button>
      </router-link>
    </section>

  </div>
</template>

<script>
import confetti from 'canvas-confetti'

export default {
  name: 'Landing',
  data() {
    return {
      // Starts as the first board's own background so the hero never renders a bare
      // white board on first paint. Overwritten by goToBoard() on selection.
      mockBg: null,   // set in created() once boards are available
      backgrounds: [
        { label: 'Floral', thumb: require('@/assets/images/floral-7887374_1280_thumb.jpg'), url: require('@/assets/images/floral-7887374_1280_thumb.jpg') },
        { label: 'Flowers', thumb: require('@/assets/images/flowers-6924088_1280_thumb.png'), url: require('@/assets/images/flowers-6924088_1280_thumb.png') },
        { label: 'Doodle', thumb: require('@/assets/images/doodle-7898618_1280_thumb.jpg'), url: require('@/assets/images/doodle-7898618_1280_thumb.jpg') },
        { label: 'Boho', thumb: require('@/assets/images/boho-style-7314646_1280_thumb.png'), url: require('@/assets/images/boho-style-7314646_1280_thumb.png') },
        { label: 'Christmas', thumb: require('@/assets/images/christmas-trees-8416168_1280_thumb.jpg'), url: require('@/assets/images/christmas-trees-8416168_1280_thumb.jpg') },
        { label: 'Bee', thumb: require('@/assets/images/bee-7666216_1280_thumb.jpg'), url: require('@/assets/images/bee-7666216_1280_thumb.jpg') },
      ],
      tags: ['Birthdays', 'Farewells', 'Work Milestones', 'New Baby', 'Anniversaries'],
      // Occasion example boards. Each mirrors the real board structure (title, meta,
      // wishes with optional GIFs) so the hero mock is a faithful preview of a real board.
      //
      // Density note: the Birthday board reads best because ~50% of its cards carry media.
      // The other occasions originally ran 20-25% media, which made them look thin beside
      // it. All six now carry 7-8 wishes with media on roughly half, matching the strongest
      // example rather than the weakest.
      boardIndex: 0,
      showSampleNote: false,
      railAtEnd: false,
      showStickyCta: false,
      scrollTicking: false,
      boards: [
        {
          occasion: 'Birthday',
          title: 'Happy Birthday, Sarah',
          meta: '24 messages · created by James',
          blurb: 'The classic. Cake, confetti and a wall of wishes everyone can add to.',
          bg: require('@/assets/images/cookies-8416230_1280_thumb.jpg'),
          messages: [
            { name: 'Priya M.', message: 'Happy birthday! Hope this year brings everything you deserve.', gif: 'https://media4.giphy.com/media/v1.Y2lkPTY4ZWI0ZjJldHV1YXR4NGpyZTZianJlbzM1ejdidGxwcXQ3ODR6Yml5ZW80bXdpeCZlcD12MV9naWZzX3NlYXJjaCZjdD1n/4ebGXcUszdrJNDoVk4/giphy.gif' },
            { name: 'James T.', message: "You've been the backbone of this team. Wishing you the very best.", gif: null },
            { name: 'Carlos R.', message: 'So lucky to work with someone as kind as you!', gif: 'https://media0.giphy.com/media/v1.Y2lkPTY4ZWI0ZjJldXEyem9kejNyM3lvdnh0eGtjdjBsOGp6bmJoOTI3cXB2NDV0c29mZCZlcD12MV9naWZzX3NlYXJjaCZjdD1n/3IaC81YOZudfa/giphy.gif' },
            { name: 'Lisa K.', message: "Here's to you — cheers!", gif: null },
            { name: 'Anika S.', message: 'Wishing you a wonderful day filled with joy.', gif: 'https://media1.giphy.com/media/v1.Y2lkPTY4ZWI0ZjJlY25kcDZjbmRld2N5cTVsMzczMXF5OHB1c3p2MWRvdmluOWxpb3RoYyZlcD12MV9naWZzX3NlYXJjaCZjdD1n/PjH7FcgYsHaSWFYhWh/giphy.gif' },
            { name: 'Tom B.', message: "Many happy returns! You're one in a million.", gif: null },
          ],
        },
        {
          occasion: 'Farewell',
          title: 'Goodbye & Good Luck, Daniel',
          meta: '38 messages · created by the Design team',
          blurb: 'A send-off that outlives the speech. Everyone writes, the team presents.',
          bg: require('@/assets/images/leaves-8184621_1280_thumb.png'),
          messages: [
            { name: 'Marcus L.', message: 'Four years of your calm in every storm. Off you go — we will miss you.', gif: null },
            { name: 'Ines R.', message: 'You taught me more in six months than anyone has in six years.', gif: 'https://media3.giphy.com/media/v1.Y2lkPTY4ZWI0ZjJlcHU3YWl3bm1nczA4OXo1eWdvNjV0b29nMGc1NmxxMzNhdW1icjd3ciZlcD12MV9naWZzX3NlYXJjaCZjdD1n/K0Q7eJHQ2Vbfq/giphy.gif' },
            { name: 'Ben C.', message: 'The desk is not the same. Neither is the coffee chat.', gif: null },
            { name: 'Yuki N.', message: 'Safe travels. Come visit and let us feed you properly.', gif: 'https://media1.giphy.com/media/v1.Y2lkPTY4ZWI0ZjJlcHU3YWl3bm1nczA4OXo1eWdvNjV0b29nMGc1NmxxMzNhdW1icjd3ciZlcD12MV9naWZzX3NlYXJjaCZjdD1n/FcaOPwBGUVpiDdSDaR/giphy.gif' },
            { name: 'Sofia A.', message: 'To new adventures. You earned every bit of this one.', gif: null },
            { name: 'Rahul D.', message: 'You never once made a deadline feel like a threat. Thank you.', gif: 'https://media3.giphy.com/media/v1.Y2lkPTY4ZWI0ZjJlYWtnaTFxMGNuZGtnNnpwdnhuaWpqeW5tOHNwbWs2OHV2d3BydXA3byZlcD12MV9naWZzX3NlYXJjaCZjdD1n/eVAyMuWfakZewrW43T/giphy.gif' },
            { name: 'Tessa V.', message: 'The office playlist was half your work. Play it loud.', gif: null },
            { name: 'Omar H.', message: 'We will miss you more than we say it here. Take care.', gif: 'https://media2.giphy.com/media/v1.Y2lkPTY4ZWI0ZjJlcHU3YWl3bm1nczA4OXo1eWdvNjV0b29nMGc1NmxxMzNhdW1icjd3ciZlcD12MV9naWZzX3NlYXJjaCZjdD1n/WV8u2LzZZFa31SzzUe/giphy.gif' },
          ],
        },
        {
          occasion: 'Work milestone',
          title: 'Congratulations on 10 Years, Aisha',
          meta: '41 messages · created by Priya',
          blurb: 'Mark a promotion or anniversary with the people who were there for it.',
          bg: require('@/assets/images/chess-8348280_1280_thumb.jpg'),
          messages: [
            { name: 'Raj P.', message: 'A decade of brilliance. Here is to the next ten.', gif: null },
            { name: 'Helen W.', message: 'You have set the bar for all of us. Grateful.', gif: 'https://media2.giphy.com/media/v1.Y2lkPTY4ZWI0ZjJlMjQyejc4cmc5cXBoOHVsYXE1dzhhbG9mOGN5djdxdDdtdWZubHRvNyZlcD12MV9naWZzX3NlYXJjaCZjdD1n/dfVwSuhfBcmRPOtlTA/giphy.gif' },
            { name: 'Dev K.', message: 'Still the first person I ask when something is hard.', gif: null },
            { name: 'Nora S.', message: 'Ten years well spent. Enjoy every minute of the next ten.', gif: null },
            { name: 'Chris O.', message: 'Congratulations! You have earned this one properly.', gif: 'https://media0.giphy.com/media/v1.Y2lkPTY4ZWI0ZjJlMjQyejc4cmc5cXBoOHVsYXE1dzhhbG9mOGN5djdxdDdtdWZubHRvNyZlcD12MV9naWZzX3NlYXJjaCZjdD1n/w7QNTIIhiQXpqFXRs7/giphy.gif' },
            { name: 'Leah B.', message: 'You mentored six of us. Two now mentor others. That compounds.', gif: null },
            { name: 'Sam T.', message: 'Ten years of you solving the impossible before breakfast.', gif: 'https://media1.giphy.com/media/v1.Y2lkPTY4ZWI0ZjJldWpmeGt1bHdweGNudnJxa2M4ejFjaHMzandtb255MjlsZnk2MGZpeiZlcD12MV9naWZzX3NlYXJjaCZjdD1n/1WDHQUxNbnnddiREC5/giphy.gif' },
            { name: 'Grace I.', message: 'A rare combination of high standards and real kindness.', gif: null },
          ],
        },
        {
          occasion: 'New baby',
          title: 'Welcome Baby Meera',
          meta: '29 messages · created by family',
          blurb: 'For the new arrivals. Gentle wishes the parents can keep forever.',
          bg: require('@/assets/images/plants-7933494_1280_thumb.jpg'),
          messages: [
            { name: 'Grandma Latha', message: 'Welcome little one. The world is lucky to have you.', gif: null },
            { name: 'Uncle Raj', message: 'Sleep well, eat well, and never ignore your mother.', gif: 'https://media4.giphy.com/media/v1.Y2lkPTY4ZWI0ZjJlcGdvMTgxams0aWptZGFtajNleDJ3MWg2cW4xaGpxM21wMXJmandqdSZlcD12MV9naWZzX3NlYXJjaCZjdD1n/21TLGIdROJLT2ERkmk/giphy.gif' },
            { name: 'Kavya', message: 'Cannot wait to meet you. Already the loveliest person here.', gif: null },
            { name: 'Aunt Meena', message: 'A whole village is delighted by your arrival.', gif: null },
            { name: 'Dr. Iyer', message: 'Healthy, happy and loved — the only three that matter.', gif: 'https://media0.giphy.com/media/v1.Y2lkPTY4ZWI0ZjJlcm9kdXVpcGQ3cWtvcWVjYjkyZ2FxcXFyYXV5YXhlcDIzYndtNjNjZSZlcD12MV9naWZzX3NlYXJjaCZjdD1n/TdoWYiDPiU3mwmUsS3/giphy.gif' },
            { name: 'Nani', message: 'Your first laugh, I am told, is at 3am. Already musical.', gif: null },
            { name: 'Vikram', message: 'Every child arrives with good luck attached. Yours looks strong.', gif: 'https://media3.giphy.com/media/v1.Y2lkPTY4ZWI0ZjJlcm9kdXVpcGQ3cWtvcWVjYjkyZ2FxcXFyYXV5YXhlcDIzYndtNjNjZSZlcD12MV9naWZzX3NlYXJjaCZjdD1n/H9Z1YvMk6AYk6OxxFZ/giphy.gif' },
            { name: 'Family', message: 'Loved already, more than you will ever know.', gif: null },
          ],
        },
        {
          occasion: 'Anniversary',
          title: 'Happy 25th Anniversary, Meera & Matt',
          meta: '33 messages · created by their children',
          blurb: 'Mark a milestone together. The keepsake outlives the party.',
          bg: require('@/assets/images/flowers-8219261_1280_thumb.png'),
          messages: [
            { name: 'Their children', message: 'Twenty-five years of choosing each other. We see it every day.', gif: null },
            { name: 'Priya', message: 'The best recipe for a long marriage: keep laughing at the same jokes.', gif: 'https://media1.giphy.com/media/v1.Y2lkPTY4ZWI0ZjJlbzZjd2J4cmM5b24zMTFkdnNtZjFsNjhpYjE2anR6OGVjMjd2dXUwMiZlcD12MV9naWZzX3NlYXJjaCZjdD1n/JnB2xdMfOce9iOojL3/giphy.gif' },
            { name: 'Dr. Rao', message: 'Congratulations. Rare is a couple who grow together.', gif: null },
            { name: 'Nikhil', message: 'Still the most boring, wonderful thing — in the best way.', gif: null },
            { name: 'Anjali', message: 'We grew up watching you disagree beautifully. Thank you.', gif: 'https://media3.giphy.com/media/v1.Y2lkPTY4ZWI0ZjJlbzZjd2J4cmM5b24zMTFkdnNtZjFsNjhpYjE2anR6OGVjMjd2dXUwMiZlcD12MV9naWZzX3NlYXJjaCZjdD1n/j9zQrU6Td5KuVAPQDM/giphy.gif' },
            { name: 'Neighbours', message: 'Twenty-five years of your doorbell and warm tea. Thank you.', gif: null },
            { name: 'Priya & Nikhil', message: 'Happy silver anniversary. Here is to twenty-five more.', gif: 'https://media1.giphy.com/media/v1.Y2lkPTY4ZWI0ZjJlbzZjd2J4cmM5b24zMTFkdnNtZjFsNjhpYjE2anR6OGVjMjd2dXUwMiZlcD12MV9naWZzX3NlYXJjaCZjdD1n/qqMybP44V9Wp4Yhv7f/giphy.gif' },
          ],
        },
        {
          occasion: 'Graduation',
          title: 'Congratulations, Amy — Class of 2026',
          meta: '36 messages · created by the department',
          blurb: 'Let classmates, teachers and family leave something that lasts.',
          bg: require('@/assets/images/ice-cream-8081692_1280_thumb.png'),
          messages: [
            { name: 'Dr. Menon', message: 'The student who asked the best questions in the room. Well done.', gif: null },
            { name: 'Arjun', message: 'Four years of late-night notes and you still beat me at everything.', gif: 'https://media0.giphy.com/media/v1.Y2lkPTY4ZWI0ZjJlN2NwMDR4ZTgxaGNpd2E4dnR4N2xuemp0eGVvNXpldThqNHRrN2d5MiZlcD12MV9naWZzX3NlYXJjaCZjdD1n/GW3nzfV1snp252tiFi/giphy.gif' },
            { name: 'Family', message: 'So proud. Go be brilliant, but come home often.', gif: null },
            { name: 'Classmates', message: 'To new beginnings. The best is still ahead.', gif: null },
            { name: 'Maya J.', message: 'You made the hardest course look like a puzzle. Remember that.', gif: 'https://media3.giphy.com/media/v1.Y2lkPTY4ZWI0ZjJlbWo4b3MwaDFraHZtZXd0c21uaHc1YXdiZ3FuZm93bHllZ2Q5MnUzOCZlcD12MV9naWZzX3NlYXJjaCZjdD1n/118jQuX0J1RcMVRwFA/giphy.gif' },
            { name: 'Ravi T.', message: 'Lifelong permission to borrow your notes. Any time, always.', gif: null },
            { name: 'Dean', message: 'The Department is proud. Go do something that frightens you.', gif: 'https://media4.giphy.com/media/v1.Y2lkPTY4ZWI0ZjJlbGZqaWNpdGNobWt0cWxwbHQ3a255aXhlNXB6MXR2bnV1cnF2NGRpdSZlcD12MV9naWZzX3NlYXJjaCZjdD1n/a2IguHnGCYHjW/giphy.gif' },
          ],
        },
      ],
      occasions: [
        { icon: 'bx-cake', title: 'Birthdays', desc: 'Collect wishes from the whole team without spoiling the surprise.' },
        { icon: 'bx-door-open', title: 'Farewells', desc: 'Give someone a send-off they\'ll carry with them always.' },
        { icon: 'bx-trophy', title: 'Work milestones', desc: 'Mark promotions, anniversaries and achievements together.' },
        { icon: 'bx-heart', title: 'New arrivals', desc: 'Welcome a new baby or team member with a flood of warm messages.' },
        { icon: 'bx-diamond', title: 'Anniversaries', desc: 'Gather messages from friends and family into one lasting keepsake.' },
        { icon: 'bx-award', title: 'Graduations', desc: 'Let classmates, teachers and family leave congratulations that last.' },
      ],
      steps: [
        { num: '01', title: 'Create your board', desc: 'Name it, pick who it\'s for. Done in thirty seconds.' },
        { num: '02', title: 'Share the link', desc: 'Anyone with the link can add a message — no account required.' },
        { num: '03', title: 'Reveal and celebrate', desc: 'Present it live or share it privately. The choice is yours.' },
      ],
      features: [
        { icon: 'bx-link-alt', title: 'No sign-up for contributors', desc: 'Anyone can add a message the moment they receive the link. Zero friction.' },
        { icon: 'bx-image-alt', title: 'GIFs and images', desc: 'Contributors can attach a GIF or image to make their message uniquely theirs.' },
        { icon: 'bx-lock-alt', title: 'Lock when ready', desc: 'Seal the board before the reveal so no last-minute messages spoil the surprise.' },
        { icon: 'bx-slideshow', title: 'Present on any screen', desc: 'Open the board in presentation mode and let the messages speak for themselves.' },
      ],
      testimonials: [
        { quote: 'We used this for our manager\'s farewell and she cried happy tears. So much more personal than a card.', name: 'Amanda R.', role: 'HR Manager' },
        { quote: 'Setup took two minutes. By the end of the day we had 34 messages from across the team. Absolutely brilliant.', name: 'Tom B.', role: 'Engineering Lead' },
        { quote: 'I\'ve used it for three birthdays now. Everyone loves that they can add a GIF. Far better than a group email.', name: 'Siona P.', role: 'Product Designer' },
      ],
    }
  },
  computed: {
    activeBoard() {
      return this.boards[this.boardIndex] || this.boards[0];
    },
    // The switcher offers the 6 presets plus the selected sample's own background.
    // Without that extra entry, a sample background is never in the list, so no
    // thumbnail can render as active and "None" wrongly shows as selected.
    bgOptions() {
      const presets = this.backgrounds;
      const own = this.activeBoard && this.activeBoard.bg;
      if (!own || presets.some(p => p.url === own)) return presets;
      return [{ label: 'Sample', thumb: own, url: own }].concat(presets);
    }
  },
  methods: {
    goToBoard(i) {
      if (i < 0 || i >= this.boards.length) return;
      this.boardIndex = i;
      this.showSampleNote = false;   // new board, clear the previous answer
      // Carry the sample's own background over, so the board looks like the example the
      // visitor just picked. Without this the board rendered with whatever the background
      // switcher had last set (or nothing), which looked broken.
      this.mockBg = this.boards[i].bg;
      this.$nextTick(() => this.scrollTileIntoView(i));
    },
    // Keep the selected tile visible in the scrolling rail.
    scrollTileIntoView(i) {
      const strip = this.$refs.occRail;
      if (!strip) return;
      const tile = strip.querySelectorAll('.occ-tile')[i];
      if (!tile) return;
      const sr = strip.getBoundingClientRect();
      const tr = tile.getBoundingClientRect();
      if (tr.bottom > sr.bottom - 4 || tr.top < sr.top + 4) {
        strip.scrollTo({ top: tile.offsetTop - strip.offsetTop, behavior: 'smooth' });
      }
    },
    onRailScroll(e) {
      const el = e.target;
      const atEnd = el.scrollTop + el.clientHeight >= el.scrollHeight - 4;
      if (atEnd !== this.railAtEnd) this.railAtEnd = atEnd;
    },
    // Sticky CTA: appears only once the hero's own button has scrolled out of view, so it
    // never duplicates something already on screen. Passive listener + rAF throttle.
    onScroll() {
      if (this.scrollTicking) return;
      this.scrollTicking = true;
      window.requestAnimationFrame(() => {
        const anchor = this.$refs.heroCta;
        let show = false;
        if (anchor) {
          const r = anchor.getBoundingClientRect();
          // Show once the hero CTA's bottom has passed above the viewport top.
          show = r.bottom < 0;
        } else {
          show = window.scrollY > 420;
        }
        if (show !== this.showStickyCta) this.showStickyCta = show;
        this.scrollTicking = false;
      });
    },
    fireConfetti() {
      // Confetti belongs to the sample board, not the page. canvas-confetti's `origin` is
      // a fraction of the VIEWPORT, so without an explicit x it fires from the centre of
      // the whole screen. Compute the board's centre as a viewport fraction so the burst
      // stays inside the mock. Falls back to 0.5 if the ref isn't mounted (first paint).
      const el = this.$refs.boardEl;
      let originX = 0.5;
      let originY = 0.42;
      if (el) {
        const r = el.getBoundingClientRect();
        const vw = window.innerWidth || document.documentElement.clientWidth;
        const vh = window.innerHeight || document.documentElement.clientHeight;
        if (vw && vh) {
          originX = Math.min(1, Math.max(0, (r.left + r.width / 2) / vw));
          originY = Math.min(1, Math.max(0, (r.top + r.height * 0.28) / vh));
        }
      }

      const burst = () => confetti({
        particleCount: 90,
        spread: 62,                    // tighter than the old 80 — stays within the board
        startVelocity: 26,
        ticks: 160,
        origin: { x: originX, y: originY },
        colors: ['#e8a020', '#f4d06f', '#ffffff', '#111111', '#f0a0a0'],
        scalar: 0.9,
        disableForReducedMotion: true,
      });

      burst();
      setTimeout(burst, 200);
    }
  },
  created() {
    // Seed the background from the first sample so the hero is never backgroundless.
    if (this.boards[0]) this.mockBg = this.boards[0].bg;
  },
  mounted() {
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.onScroll();
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.onScroll);
  }
}
</script>

<style scoped>
*, *::before, *::after { box-sizing: border-box; }

.landing {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  color: #111;
  overflow-x: hidden;
  background: #fff;
}

/* ── OVERLINE ── */
.overline {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 2.5px;
  text-transform: uppercase;
  color: #e8a020;
  text-align: center;
  margin-bottom: 10px;
}

/* ── HERO ── */
.hero-split {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 64px;
  align-items: center;
  max-width: 1600px;
  margin: 0 auto;
  padding: 88px 40px;
  min-height: 88vh;
}

.use-cases {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 28px;
}

.tag {
  font-size: 0.72rem;
  font-weight: 600;
  padding: 4px 12px;
  background: #fdf3e0;
  border-radius: 4px;
  color: #b07010;
  letter-spacing: 0.2px;
  border: 1px solid #f0d898;
}

.hero-left h1 {
  font-size: 2.4rem;
  font-weight: 800;
  line-height: 1.12;
  letter-spacing: -0.8px;
  margin-bottom: 22px;
  color: #0a0a0a;
}

.hero-sub {
  font-size: 1.05rem;
  color: #555;
  line-height: 1.72;
  margin-bottom: 36px;
  /* The hero widened to 1600px, so each column is ~768px. Without a cap this
     paragraph would set to ~750px per line, well past a comfortable measure.
     560px matches .hero-right and .section-heading. */
  max-width: 560px;
  margin-left: auto;
  margin-right: auto;
}

.hero-cta {
  display: flex;
  flex-direction: column;
  align-items: center;   /* centred under the hero copy, not ragged-left */
  gap: 14px;
  text-align: center;
}

.cta-link { display: inline-block; }

.btn-cta {
  padding: 18px 46px;      /* was 15px 36px — the primary CTA should lead the page */
  background: #0a0a0a;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 1.05rem;      /* was 0.95rem */
  font-weight: 700;
  letter-spacing: -0.1px;
  cursor: pointer;
  transition: background 0.18s, transform 0.15s, box-shadow 0.18s;
  box-shadow: 0 2px 10px rgba(0,0,0,0.14);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.btn-cta:hover {
  background: #2a2a2a;
  transform: translateY(-2px);
  box-shadow: 0 8px 22px rgba(0,0,0,0.2);
}
.btn-cta:active { transform: translateY(0); }
.btn-cta:focus-visible { outline: 2px solid #e8a020; outline-offset: 3px; }

.cta-note {
  font-size: 0.78rem;
  color: #aaa;
}

/* ── MOCK BOARD ── */
/* Board first, occasion tiles to its right. Tiles are the navigation: the board itself
   carries no overlay chrome, so it reads as a real board rather than a widget. */
.hero-right {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  width: 100%;
  max-width: 560px;
  margin: 0 auto;
}

.occ-rail-v {
  /* order:2 puts the tiles AFTER the board (board is order:1). Without an explicit
     order the rail defaults to 0 and renders first — it measured x=752 while the
     board started at x=854. */
  order: 2;
  /* Hard width + min-width:0 so the tiles stay a slim strip. Without min-width:0 the
     flex item refuses to shrink and the board gets squeezed instead — this measured
     305px of tiles against a 207px board. */
  flex: 0 0 88px;
  width: 88px;
  min-width: 0;
  /* Crucially: do NOT stretch to the board's height. Without this the strip collapsed
     to 70px against a 791px board, so all six tiles were squeezed in and nothing
     scrolled. align-self keeps the rail at its natural height. */
  align-self: flex-start;
  display: flex;
  flex-direction: column;
  gap: 8px;   /* space between the title and the scrolling strip */
}
.occ-rail-v-title {
  font-size: 0.6rem;
  line-height: 1.45;
  color: #999;
  margin: 0 0 2px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

/* ── STICKY CTA ── */
/* Bottom-centre pill that follows the reader once the hero CTA is out of view.
   Slightly smaller than the hero button so it reads as a convenience, not a
   competing primary. */
.sticky-cta {
  position: fixed;
  left: 50%;
  bottom: 26px;
  transform: translateX(-50%);
  z-index: 40;
  display: inline-block;
}
.btn-cta-sm {
  padding: 13px 30px;
  font-size: 0.92rem;
  border-radius: 24px;
  box-shadow: 0 6px 20px rgba(0,0,0,0.22);
}
.btn-cta-sm:hover { transform: translateY(-2px); }

.sticky-cta-enter-active, .sticky-cta-leave-active {
  transition: opacity 0.28s ease, transform 0.28s ease;
}
.sticky-cta-enter-from, .sticky-cta-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(12px);
}
@media (prefers-reduced-motion: reduce) {
  .sticky-cta-enter-active, .sticky-cta-leave-active { transition: none; }
}
/* Keep it clear of the page footer / mobile home indicator */
@media (max-width: 600px) {
  .sticky-cta { bottom: 18px; }
  .btn-cta-sm { padding: 12px 26px; font-size: 0.88rem; }
}

/* ── OCCASION RAIL (scrolling strip) ── */
.occ-rail-scroll {
  /* Only ~3 tiles visible; the rest scroll. A bottom mask hints there's more
     without adding an arrow, which would read as clutter. */
  max-height: 214px;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-width: none;
  padding-right: 2px;
  /* This element is itself a flex child of .occ-rail-v and was being compressed to
     fit — it measured 70px tall with scrollHeight 70px, so nothing actually scrolled.
     flex: 0 0 auto + align-self: flex-start keeps it at its natural content height. */
  flex: 0 0 auto;
  align-self: flex-start;
  width: 100%;
  /* column so the tiles stack and honour flex: 0 0 66px */
  display: flex;
  flex-direction: column;
  gap: 8px;
  /* fade the bottom edge so a cut-off tile reads as "more below" */
  -webkit-mask-image: linear-gradient(to bottom, #000 78%, rgba(0,0,0,0.15) 100%);
  mask-image: linear-gradient(to bottom, #000 78%, rgba(0,0,0,0.15) 100%);
}
.occ-rail-scroll::-webkit-scrollbar { display: none; }
.occ-rail-scroll:focus-visible { outline: 2px solid #e8a020; outline-offset: 3px; border-radius: 8px; }

.occ-tile {
  order: 2;
  position: relative;
  height: 66px;
  /* Don't compress when the strip scrolls — without this the tiles squash vertically. */
  flex: 0 0 66px;
  border-radius: 10px;
  border: 2px solid transparent;
  background-size: cover;
  background-position: center;
  cursor: pointer;
  padding: 0;
  overflow: hidden;
  transition: transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
  box-shadow: 0 1px 4px rgba(0,0,0,0.08);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}
/* Dark scrim so the label stays legible over any background image. */
.occ-tile::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.12) 60%, rgba(0,0,0,0) 100%);
}
.occ-tile-label {
  position: absolute;
  left: 7px;
  bottom: 6px;
  z-index: 2;
  color: #fff;
  font-size: 0.58rem;
  font-weight: 800;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  text-align: left;
  line-height: 1.2;
}
/* Nudge LEFT on hover — the tiles now sit to the right of the board. */
.occ-tile:hover { transform: translateX(-3px); }
.occ-tile.active {
  border-color: #e8a020;
  box-shadow: 0 4px 14px rgba(232,160,32,0.35);
  transform: translateX(-3px);
}
.occ-tile:focus-visible { outline: 2px solid #e8a020; outline-offset: 2px; }

.mock-board {
  order: 1;
  flex: 1 1 auto;
  min-width: 0;
  background: #fff;
  border: 1px solid #e4e4e4;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0,0,0,0.04), 0 20px 56px rgba(0,0,0,0.09);
  width: 100%;
  max-width: 440px;
  transition: background-image 0.3s;
}

.mock-board-top {
  padding: 18px 20px 14px;
  border-bottom: 1px solid rgba(240,240,240,0.8);
  text-align: center;
  background: rgba(255,255,255,0.88);
  backdrop-filter: blur(4px);
}

.mock-board-title {
  font-size: 1rem;
  font-weight: 800;
  color: #0a0a0a;
  margin-bottom: 3px;
  letter-spacing: -0.2px;
}

.mock-board-meta {
  font-size: 0.7rem;
  color: #bbb;
  margin-bottom: 12px;
}

.mock-actions {
  display: flex;
  justify-content: center;
  gap: 8px;
}

.mock-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: #0a0a0a;
  color: #fff;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 7px 14px;
  border-radius: 6px;
  cursor: default;
}

.mock-btn-ghost {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  color: #888;
  font-size: 1rem;
  cursor: pointer;
  background: rgba(255,255,255,0.9);
  transition: border-color 0.15s, color 0.15s;
}

.mock-btn-ghost:hover {
  border-color: #e8a020;
  color: #e8a020;
}

/* Sample-board affordances: these are previews, not live controls. Muted so nothing
   on the board reads as an interactive element that does nothing when tapped. */
.mock-btn-preview {
  background: rgba(10,10,10,0.88);
  color: #fff;
  cursor: pointer;          /* it responds now, so it should look tappable */
  border: none;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  transition: background 0.18s;
}
.mock-btn-preview:hover { background: rgba(10,10,10,0.96); }
.mock-btn-preview:focus-visible { outline: 2px solid #e8a020; outline-offset: 2px; }

/* ── SAMPLE BOARD INVITE ── */
/* Appears under the board header when someone taps "Add a wish" on a sample board.
   Deliberately quiet: small, muted, one line, no button competing with the real CTA.
   Amber left-edge rule ties it to the selection accent without shouting. */
.mock-invite {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  margin: 10px 0 0;
  padding: 8px 12px;
  font-size: 0.73rem;
  line-height: 1.45;
  color: #6b6257;
  text-align: center;
  background: rgba(253, 243, 224, 0.9);
  border: 1px solid rgba(232, 160, 32, 0.28);
  border-radius: 8px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}
.mock-invite i { font-size: 0.95rem; color: #b07010; flex-shrink: 0; }

.mock-note-fade-enter-active, .mock-note-fade-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}
.mock-note-fade-enter-from, .mock-note-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
@media (prefers-reduced-motion: reduce) {
  .mock-note-fade-enter-active, .mock-note-fade-leave-active { transition: none; }
}

/* ── BACKGROUND SWITCHER ── */
.bg-switcher {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  background: rgba(255,255,255,0.88);
  backdrop-filter: blur(4px);
  border-bottom: 1px solid rgba(240,240,240,0.8);
}

.bg-label {
  font-size: 0.65rem;
  font-weight: 700;
  color: #bbb;
  text-transform: uppercase;
  letter-spacing: 1px;
  white-space: nowrap;
}

.bg-thumbs {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.bg-thumb {
  width: 26px;
  height: 26px;
  border-radius: 5px;
  border: 2px solid transparent;
  background-size: cover;
  background-position: center;
  cursor: pointer;
  transition: border-color 0.15s, transform 0.15s;
  padding: 0;
}

.bg-thumb:hover { transform: scale(1.15); }
.bg-thumb.active { border-color: #e8a020; }

.bg-thumb-none {
  background: #f4f4f4;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  color: #bbb;
}

/* ── MOCK CARDS ── */
.mock-cards {
  padding: 14px;
  columns: 2;
  column-gap: 10px;
  background: transparent;
}

.mock-card {
  break-inside: avoid;
  background: rgba(255,255,255,0.88);
  border: 1px solid rgba(239,239,239,0.7);
  border-radius: 10px;
  padding: 13px;
  margin-bottom: 10px;
  backdrop-filter: blur(2px);
}

.mock-from {
  font-size: 0.65rem;
  font-weight: 700;
  color: #ccc;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  margin-bottom: 6px;
}

.mock-msg {
  font-size: 0.78rem;
  color: #444;
  line-height: 1.55;
}

.mock-gif {
  width: 100%;
  border-radius: 6px;
  margin-top: 8px;
  display: block;
}

/* ── SECTIONS ── */
.section-pad {
  padding: 88px 40px;
  max-width: 1600px;
  margin: 0 auto;
}

.section-alt {
  background: #fafafa;
  border-top: 1px solid #efefef;
  border-bottom: 1px solid #efefef;
  padding: 88px 40px;
}

.section-inner {
  max-width: 1600px;
  margin: 0 auto;
}

.section-heading {
  font-size: 1.7rem;
  font-weight: 800;
  text-align: center;
  margin-bottom: 56px;
  letter-spacing: -0.4px;
  color: #0a0a0a;
  max-width: 540px;
  margin-left: auto;
  margin-right: auto;
}

/* ── RESPONSIVE ── */
@media (prefers-reduced-motion: reduce) {
  .occ-tile { transition: none; }
}

/* Tiles stay usable on touch: no hover-reveal, and a larger tap target. */
@media (max-width: 768px), (hover: none) {
  .occ-tile { height: 58px; flex-basis: 58px; }
}

@media (max-width: 600px) {
  .mock-cards { columns: 2; padding: 10px; }
  .mock-board-title { font-size: 0.95rem; }
  .occ-rail-v { flex-basis: 78px; width: 78px; }
  .occ-tile { height: 52px; flex-basis: 52px; }
  .occ-rail-v-title { display: none; }
  /* Show ~3 small tiles rather than 3 large ones. */
  .occ-rail-scroll { max-height: 178px; }
}

/* ── OCCASIONS ── */
.occasion-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 36px;
}

.occasion { padding: 0 8px; }

.occ-icon {
  font-size: 1.3rem;
  color: #e8a020;
  display: block;
  margin-bottom: 12px;
}

.occasion h3 {
  font-size: 0.88rem;
  font-weight: 700;
  margin-bottom: 7px;
  color: #111;
}

.occasion p {
  font-size: 0.82rem;
  color: #777;
  line-height: 1.6;
}

/* ── STEPS ── */
.steps {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 48px;
}

.step { text-align: center; }

.step-num {
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 2.5px;
  color: #e8a020;
  margin-bottom: 14px;
  display: block;
}

.step h3 {
  font-size: 0.97rem;
  font-weight: 700;
  margin-bottom: 9px;
  color: #111;
}

.step p {
  font-size: 0.84rem;
  color: #777;
  line-height: 1.65;
}

/* ── FEATURES ── */
.features {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 40px;
}

.feature-icon-wrap {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  background: #fdf3e0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}

.feature-icon-wrap i { font-size: 1.2rem; color: #e8a020; }

.feature h3 {
  font-size: 0.9rem;
  font-weight: 700;
  margin-bottom: 8px;
  color: #111;
}

.feature p {
  font-size: 0.82rem;
  color: #777;
  line-height: 1.62;
}

/* ── TESTIMONIALS ── */
.testimonials {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
}

.testimonial {
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  padding: 32px;
}

.quote-icon {
  font-size: 1.4rem;
  color: #e8a020;
  display: block;
  margin-bottom: 14px;
}

.quote {
  font-size: 0.9rem;
  color: #333;
  line-height: 1.72;
  margin-bottom: 24px;
}

.author { display: flex; align-items: center; gap: 12px; }

.author-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #0a0a0a;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.82rem;
  flex-shrink: 0;
}

.author-name { font-size: 0.84rem; font-weight: 700; color: #111; }
.author-role { font-size: 0.74rem; color: #aaa; margin-top: 2px; }

/* ── BOTTOM CTA ── */
.cta-bottom {
  text-align: center;
  padding: 96px 40px;
  border-top: 1px solid #efefef;
}

.cta-bottom h2 {
  font-size: 1.9rem;
  font-weight: 800;
  margin-bottom: 12px;
  letter-spacing: -0.4px;
  color: #0a0a0a;
}

.cta-bottom p {
  font-size: 0.92rem;
  color: #888;
  margin-bottom: 36px;
}

/* ── RESPONSIVE ── */
@media (max-width: 860px) {
  .hero-split {
    grid-template-columns: 1fr;
    padding: 56px 24px;
    min-height: auto;
    gap: 40px;
  }
  .hero-left { text-align: center; }
  .hero-left h1 { font-size: 1.9rem; }
  .use-cases { justify-content: center; }
  .hero-cta { align-items: center; }
  .section-pad { padding: 64px 24px; }
  .section-alt { padding: 64px 24px; }
  .cta-bottom { padding: 64px 24px; }
}

@media (max-width: 480px) {
  .hero-left h1 { font-size: 1.6rem; }
  .mock-cards { columns: 1; }
}
</style>
